/**
 * Klient API przeglądarki bazy tenanta (panel ITE, tylko odczyt).
 *
 * Trasy: `/api/admin/tenants/:tenantId/database/{schema,object,query}` — walidację zapytań,
 * maskowanie i ograniczenie do schematu tenanta robi serwer.
 */

import axios from 'axios';

import { api } from '@/lib/api';

import type {
  DbObjectDetails,
  DbObjectRef,
  DbQueryError,
  DbQueryErrorCode,
  DbQueryResult,
  DbSchemaSnapshot,
} from './types';

const base = (tenantId: string) => `/admin/tenants/${tenantId}/database`;

export async function fetchDbSchema(tenantId: string): Promise<DbSchemaSnapshot> {
  const res = await api.get<DbSchemaSnapshot>(`${base(tenantId)}/schema`);
  return res.data;
}

export async function fetchDbObject(tenantId: string, ref: DbObjectRef): Promise<DbObjectDetails> {
  const res = await api.get<DbObjectDetails>(`${base(tenantId)}/object`, {
    params: { kind: ref.kind, name: ref.name, args: 'args' in ref ? ref.args : undefined },
  });
  return res.data;
}

/** Błąd zapytania z kodem walidatora/Postgresa — rzucany przez `runDbQuery`. */
export class DbQueryFailure extends Error {
  constructor(public readonly detail: DbQueryError) {
    super(detail.message);
  }
}

const KNOWN_QUERY_ERRORS = new Set<string>([
  'SQL_EMPTY',
  'SQL_NOT_READONLY',
  'SQL_MULTIPLE_STATEMENTS',
  'SQL_PARSE_ERROR',
  'SQL_FORBIDDEN_OBJECT',
  'SQL_SENSITIVE_COLUMN',
  'SQL_FORBIDDEN_FUNCTION',
  'SQL_TIMEOUT',
  'SQL_EXECUTION_FAILED',
  'RATE_LIMITED',
]);

export async function runDbQuery(
  tenantId: string,
  input: { sql: string; limit: number; explain: boolean },
): Promise<DbQueryResult> {
  try {
    const res = await api.post<DbQueryResult>(`${base(tenantId)}/query`, input);
    return res.data;
  } catch (err) {
    if (axios.isAxiosError<{ error?: string; message?: string; position?: number }>(err)) {
      const data = err.response?.data;
      if (data?.error) {
        // Kod spoza słownika komunikatów (np. TENANT_NOT_AVAILABLE) → „baza zwróciła błąd".
        const code = (
          KNOWN_QUERY_ERRORS.has(data.error) ? data.error : 'SQL_EXECUTION_FAILED'
        ) as DbQueryErrorCode;
        throw new DbQueryFailure({
          error: code,
          message: data.message ?? data.error,
          position: data.position,
        });
      }
    }
    throw err;
  }
}
